import {
  authControllerLogoutByPost,
  AuthError,
} from '@safc/api-client';
import { throttle } from 'es-toolkit/compat';
import {
  createContext,
  useContext,
  useEffect,
} from 'react';
import { cookies } from 'shared/utils/cookie.js';
import { getMultiModalConfig } from 'shared/utils/multiModalConfig.js';

import { multiModalUrl } from '../../../env.js';

type AuthContextProps = {
  isAuthenticated: boolean;
};
export const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
});

export const authError = {
  ...AuthError,
  inactivity: 'inactivity' as const,
};

// We use session storage for that, because we want it to be reset on page reload
const lastActivityStore = {
  get(): Date | null {
    const stored = sessionStorage.getItem('lastActivity');
    return stored ? new Date(Number(stored)) : null;
  },
  set(value: Date) {
    sessionStorage.setItem('lastActivity', value.getTime().toString());
  },
};

const decodeJwt = (jwtString: string) => {
  const base64Url = jwtString.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(window.atob(base64).split('').map((c) => {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));

  return JSON.parse(jsonPayload);
};

const getAuthTokenPayload = (): { claims?: { userId?: string }; exp: number } | null => {
  const cookieValue = cookies.authToken.get();

  if (!cookieValue) {
    return null;
  }

  return decodeJwt(cookieValue);
};

const logoutDueToInactivity = () => {
  const returnUrl = new URL(window.origin);
  returnUrl.searchParams.set('authError', authError.inactivity);

  void authControllerLogoutByPost().catch(() => {}) // redirect nevertheless of error
    .then(() => {
      window.location.href = returnUrl.href;
    });
};

/** CALL THAT ONLY ONCE IN APPLICATION */
export const useAuthContext = (): AuthContextProps => {
  const isAuthenticated = Boolean(getAuthTokenPayload()?.claims?.userId);

  // ===== Checking if jwt is expired or not, and trigger similar action to auto logout  =====
  useEffect(() => {
    if (getMultiModalConfig() !== undefined) {
      // in MMR mode do not auto-logout on expired JWT
      return;
    }

    const minuteInMs = 60 * 1000;
    const intervalId = setInterval(() => {
      const authTokenPayload = getAuthTokenPayload();

      if (!authTokenPayload && isAuthenticated) {
        // Cookie was removed so it means browser removed it
        logoutDueToInactivity();
      }
    }, minuteInMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  // ===== Auto logout =====
  // Here we perform logout after some inactivity.
  // Inactivity can be checked below - we rely on clicks in the application
  useEffect(() => {
    if (getMultiModalConfig() !== undefined) {
      // in MMR mode do not auto-logout after inactivity
      return;
    }

    const twoHoursInMs = 2 * 3600 * 1000;
    const intervalId = setInterval(() => {
      const lastActivity = lastActivityStore.get();

      if (!lastActivity) {
        return;
      }

      if (lastActivity.getTime() + twoHoursInMs < Date.now()) {
        if (isAuthenticated) {
          logoutDueToInactivity();
        }
      }
    }, 3000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  // ===== Track activity =====
  // Track activity to prevent logout
  // This is simplest and most effective method
  // Using API calls for that doesn't make sense, because
  // things are periodically refetched in background
  useEffect(() => {
    const handler = throttle(() => {
      lastActivityStore.set(new Date());
    }, 5000);

    document.body.addEventListener('click', handler);
    lastActivityStore.set(new Date());

    return () => document.body.removeEventListener('click', handler);
  }, []);

  return {
    isAuthenticated,
  };
};

const loginDirect = async (options: { returnTo?: string } = {}) => {
  const url = new URL('/api/auth/login', window.location.origin);

  if (options.returnTo) {
    url.searchParams.set('continuePath', options.returnTo);
  }

  window.location.replace(url);
};

export const useAuth = () => {
  const tokenData = useContext(AuthContext);
  return {
    ...tokenData,
    loginDirect,
    login: async (options: { returnTo?: string } = {}) => {
      if (multiModalUrl) {
        const signInUrl = new URL('/signin', multiModalUrl);
        signInUrl.searchParams.set('app', 'safc');

        window.location.replace(signInUrl);
        return;
      }

      return loginDirect(options);
    },
    logout: async ({ redirectTo }: { redirectTo: string }) => {
      await authControllerLogoutByPost().catch(() => {}); /** if that action failed, still logout */
      sessionStorage.clear();
      const url = new URL(redirectTo, window.location.origin);
      window.location.replace(url);
    },
  };
};
